import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useAuth } from "../components/AuthProvider";
import { apiFetch } from "../services/api";
import { useRuntimeConfig } from "./RuntimeConfigContext";

export type FrameworkConfiguration = {
  security_level_active: boolean;
  stride_active: boolean;
  mitre_active: boolean;
  capec_active: boolean;
  iec62443_active: boolean;
  cra_active: boolean;
};

export const DEFAULT_FRAMEWORK_CONFIGURATION: FrameworkConfiguration = {
  security_level_active: true,
  stride_active: true,
  mitre_active: true,
  capec_active: true,
  iec62443_active: true,
  cra_active: true,
};

type FrameworkConfigurationContextValue = {
  configuration: FrameworkConfiguration;
  availability: FrameworkConfiguration;
  loading: boolean;
  loadError: string | null;
  save: (configuration: FrameworkConfiguration) => Promise<FrameworkConfiguration>;
  reload: () => Promise<void>;
};

const FrameworkConfigurationContext = createContext<FrameworkConfigurationContextValue>({
  configuration: DEFAULT_FRAMEWORK_CONFIGURATION,
  availability: DEFAULT_FRAMEWORK_CONFIGURATION,
  loading: false,
  loadError: null,
  save: async (configuration) => configuration,
  reload: async () => undefined,
});

function availabilityFromFeatures(features: Record<string, boolean>): FrameworkConfiguration {
  return {
    security_level_active: features.security_levels !== false,
    stride_active: features.stride !== false,
    mitre_active: features.mitre_attack !== false,
    capec_active: features.capec !== false,
    iec62443_active: features.iec62443 !== false,
    cra_active: features.cra !== false,
  };
}

function applyAvailability(
  configuration: FrameworkConfiguration,
  availability: FrameworkConfiguration
): FrameworkConfiguration {
  return {
    security_level_active: configuration.security_level_active && availability.security_level_active,
    stride_active: configuration.stride_active && availability.stride_active,
    mitre_active: configuration.mitre_active && availability.mitre_active,
    capec_active: configuration.capec_active && availability.capec_active,
    iec62443_active: configuration.iec62443_active && availability.iec62443_active,
    cra_active: configuration.cra_active && availability.cra_active,
  };
}

export function FrameworkConfigurationProvider({ children }: { children: React.ReactNode }) {
  const { tokens } = useAuth();
  const { features } = useRuntimeConfig();
  const [storedConfiguration, setStoredConfiguration] = useState<FrameworkConfiguration>(DEFAULT_FRAMEWORK_CONFIGURATION);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const availability = useMemo(() => availabilityFromFeatures(features), [features]);
  const configuration = useMemo(
    () => applyAvailability(storedConfiguration, availability),
    [storedConfiguration, availability]
  );

  const reload = useCallback(async () => {
    if (!tokens?.access_token) {
      setStoredConfiguration(DEFAULT_FRAMEWORK_CONFIGURATION);
      setLoadError(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const next = await apiFetch<FrameworkConfiguration>(
        "/api/instance-configuration",
        { method: "GET" },
        tokens.access_token
      );
      setStoredConfiguration({ ...DEFAULT_FRAMEWORK_CONFIGURATION, ...next });
    } catch (error: any) {
      setStoredConfiguration(DEFAULT_FRAMEWORK_CONFIGURATION);
      setLoadError(error?.message ?? "Failed to load framework configuration");
    } finally {
      setLoading(false);
    }
  }, [tokens?.access_token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (next: FrameworkConfiguration) => {
      if (!tokens?.access_token) throw new Error("Authentication required");
      const saved = await apiFetch<FrameworkConfiguration>(
        "/api/instance-configuration",
        { method: "PUT", body: JSON.stringify(next) },
        tokens.access_token
      );
      setStoredConfiguration({ ...DEFAULT_FRAMEWORK_CONFIGURATION, ...saved });
      setLoadError(null);
      return applyAvailability({ ...DEFAULT_FRAMEWORK_CONFIGURATION, ...saved }, availability);
    },
    [availability, tokens?.access_token]
  );

  const value = useMemo(
    () => ({ configuration, availability, loading, loadError, save, reload }),
    [configuration, availability, loading, loadError, save, reload]
  );

  return (
    <FrameworkConfigurationContext.Provider value={value}>
      {children}
    </FrameworkConfigurationContext.Provider>
  );
}

export function useFrameworkConfiguration() {
  return useContext(FrameworkConfigurationContext);
}
